import { isOpenPartnerProductOrderItemMemoInputModal } from "@/atoms/item/is-open-partner-product-order-item-memo-input-modal";
import { isOpenPartnerProductOrderItemModal } from "@/atoms/item/is-open-partner-product-order-item-modal";
import { partnerProductOrderItemPageState } from "@/atoms/item/partner-product-order-item-page-state";
import { selectedPartnerProductOrderCartItemsState } from "@/atoms/item/selected-partner-product-order-cart-items-state";
import { GLOBAL_ERROR } from "@/common/constant/global-error";
import { GrayMinusButton } from "@/components/button/GrayMinusButton";
import { GrayPlusButton } from "@/components/button/GrayPlusButton";
import { StarOutlineIcon } from "@/components/icon/StarOutlineIcon";
import { StarSolidIcon } from "@/components/icon/StarSolidIcon";
import { Body14RegularInput } from "@/components/input/Body14RegularInput";
import { Body14Medium } from "@/components/typography/Body14Medium";
import { Body14Regular } from "@/components/typography/Body14Regular";
import { Body14Strong } from "@/components/typography/Body14Strong";
import { Body14Underline } from "@/components/typography/Body14Underline";
import { itemService } from "@/services/item-service";
import { Table } from "flowbite-react";
import { useState } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { formatCommasNumber } from "../../../common/util/format-number-with-commas";
import { ItemDetailDto } from "../../../types/dto/item-detail-dto";
import { ItemDetailLineDto } from "../../../types/dto/item-detail-line-dto";
import { CustomCell } from "../CustomCell";

interface Props {
  data: ItemDetailDto;
  index: number;
  startIndex: number;
  gradeDiscountRate: number;
}

// 상품 테이블 한 줄
export const PartnerProductOrderTableRow: React.FC<Props> = ({
  data,
  index,
  startIndex,
  gradeDiscountRate,
}) => {
  const [line, setLine] = useState<ItemDetailLineDto>({
    childItem: data,
    quantity: data.minOrderQuantity,
  });
  const setPage = useSetRecoilState(partnerProductOrderItemPageState);
  const setIsPartnerProductOrderItemModalOpen = useSetRecoilState(
    isOpenPartnerProductOrderItemModal
  );
  const setIsPartnerProductOrderItemMemoInputModalOpen = useSetRecoilState(
    isOpenPartnerProductOrderItemMemoInputModal
  );
  const [cartItems, setCartItems] = useRecoilState(
    selectedPartnerProductOrderCartItemsState
  );
  const isInCart = cartItems.some(
    (cartItem) => cartItem.childItem.id === line.childItem.id
  );

  const handleFavoriteClick = async () => {
    try {
      const response = line.childItem.isStar
        ? await itemService.deleteStar(line.childItem.id)
        : await itemService.star(line.childItem.id);

      if (!response.ok) {
        return alert(GLOBAL_ERROR);
      }

      const newItem = await itemService.getById({ id: line.childItem.id });
      if (!newItem.ok) {
        return alert(GLOBAL_ERROR);
      }

      setPage((prev) => ({
        ...prev,
        contents: prev.contents.map((item) =>
          item.id === newItem.data.id ? newItem.data : item
        ),
      }));
      setLine((prev) => ({ ...prev, childItem: newItem.data }));
    } catch (error) {
      console.log(error);
    }
  };

  const handleCartClick = async (itemId: number) => {
    if (!line.childItem.isForSale) {
      return;
    }

    const removeFromCart = () => {
      const newCartItems = cartItems.filter(
        (cartItem) => cartItem.childItem.id !== line.childItem.id
      );
      setCartItems(newCartItems);
    };

    if (isInCart) {
      removeFromCart();
      return;
    }

    if (line.childItem.memos.length === 0) {
      setCartItems([...cartItems, line]);
      return;
    }

    try {
      const response = await itemService.getById({ id: itemId });
      if (response.ok) {
        setIsPartnerProductOrderItemMemoInputModalOpen({
          item: response.data,
          quantity: line.quantity,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleItemNameClick = async (itemId: number) => {
    try {
      const response = await itemService.getById({ id: itemId });
      if (response.ok) {
        setIsPartnerProductOrderItemModalOpen(response.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleMinusButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    if (isInCart || !line.childItem.isForSale) {
      return;
    }

    const newLine = {
      ...line,
      quantity: Math.max(line.quantity - 1, line.childItem.minOrderQuantity),
    };
    setLine(newLine);
  };

  const handlePlusButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    if (isInCart || !line.childItem.isForSale) {
      return;
    }

    const newLine = { ...line, quantity: line.quantity + 1 };
    setLine(newLine);
  };

  const handleQuantityChange = (value: string) => {
    const numberValue = Number(value);

    const newLine = {
      ...line,
      quantity: numberValue,
    };
    setLine(newLine);
  };

  const getCartText = () => {
    if (!line.childItem.isForSale) {
      return "품절된 상품";
    }
    return isInCart ? "장바구니 담음" : "장바구니 담기";
  };

  const getCartTextColor = () => {
    if (!line.childItem.isForSale) {
      return "text-semantic-text-disabled";
    }
    return isInCart ? "text-semantic-text-disabled" : "text-secondary-2-orange";
  };

  const getCartTextCursor = () => {
    if (!line.childItem.isForSale) {
      return "cursor-not-allowed";
    }
    return "cursor-pointer";
  };

  const getQuantityTextColor = () => {
    if (!line.childItem.isForSale) {
      return "text-semantic-text-disabled";
    }
    return isInCart ? "text-semantic-text-disabled" : "text-semantic-text-body";
  };

  return (
    <Table.Row
      key={line.childItem.id}
      className={
        isInCart || !line.childItem.isForSale
          ? "bg-grayscale-contrast-2"
          : "bg-primary-white"
      }
    >
      <Table.Cell onClick={handleFavoriteClick}>
        <CustomCell>
          {data.isStar ? (
            <StarSolidIcon size={24} color="text-secondary-2-orange" />
          ) : (
            <StarOutlineIcon size={24} color="text-grayscale-contrast-50" />
          )}
        </CustomCell>
      </Table.Cell>
      <Table.Cell>
        <CustomCell>
          <Body14Regular className="text-semantic-text-body">
            {line.childItem.category.name.replaceAll("・", "/")}
          </Body14Regular>
        </CustomCell>
      </Table.Cell>
      <Table.Cell
        className="cursor-pointer"
        onClick={() => handleItemNameClick(line.childItem.id)}
      >
        <CustomCell className="max-w-[315px] flex items-center">
          {line.childItem.imageUrls.length > 0 && (
            <img
              src={line.childItem.imageUrls[0]}
              alt={line.childItem.name}
              className="w-[32px] mr-[8px] flex-shrink-0"
            />
          )}
          <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
            <Body14Underline className="block truncate text-semantic-text-body">
              {line.childItem.name}
            </Body14Underline>
            {line.childItem.memos.length > 0 && (
              <p className="mt-1 text-xs">
                {line.childItem.memos.length}개의 상품 메모
              </p>
            )}
          </div>
        </CustomCell>
      </Table.Cell>
      <Table.Cell>
        <CustomCell className="justify-center">
          <Body14Regular className="text-semantic-text-body">
            {line.childItem.discountRate > gradeDiscountRate
              ? `${line.childItem.discountRate}%`
              : `${gradeDiscountRate}%`}
          </Body14Regular>
        </CustomCell>
      </Table.Cell>
      <Table.Cell>
        <CustomCell className="justify-center h-fit">
          <Body14Regular className="text-primary-pink max-w-[100px] whitespace-break-spaces">
            {line.childItem.incentiveDiscount?.discountRate || "N/A"}
            {line.childItem.incentiveDiscount?.discountRate && "%"}
          </Body14Regular>
        </CustomCell>
      </Table.Cell>
      <Table.Cell>
        <CustomCell className="justify-center">
          <Body14Medium className="text-secondary-2-red">
            {line.childItem.totalDiscountRate}%{" "}
          </Body14Medium>
          <Body14Medium className="text-gray-900 ml-[4px]">
            {`${formatCommasNumber(line.childItem.totalPrice)}원`}
          </Body14Medium>
        </CustomCell>
      </Table.Cell>
      <Table.Cell className="flex items-center">
        <CustomCell>
          <GrayMinusButton
            handleClick={handleMinusButtonClick}
            disabled={isInCart || !line.childItem.isForSale}
          />
          <Body14RegularInput
            minNumber={line.childItem.minOrderQuantity}
            disabled={isInCart || !line.childItem.isForSale}
            className={`${getQuantityTextColor()} mx-[10px]`}
            onChange={(value) => handleQuantityChange(value)}
          >
            {line.quantity}
          </Body14RegularInput>
          <GrayPlusButton
            handleClick={handlePlusButtonClick}
            disabled={isInCart || !line.childItem.isForSale}
          />
          <div
            onClick={() => handleCartClick(line.childItem.id)}
            className={getCartTextCursor()}
          >
            <Body14Strong className={`${getCartTextColor()} ml-[16px]`}>
              {getCartText()}
            </Body14Strong>
          </div>
        </CustomCell>
      </Table.Cell>
    </Table.Row>
  );
};
